import { DateRangeFields } from '@src/main/modules/messages/ui/mailbox/components/new-message/fields/DateRangeFields';
import { FormButtons } from '@src/main/modules/messages/ui/mailbox/components/new-message/fields/FormButtons';
import { MessageEditor } from '@src/main/modules/messages/ui/mailbox/components/new-message/fields/MessageEditor';
import PlateField from '@src/main/modules/messages/ui/mailbox/components/new-message/fields/PlateField';
import RecipientInput from '@src/main/modules/messages/ui/mailbox/components/new-message/fields/RecipientInput';
import { RecipientSelector } from '@src/main/modules/messages/ui/mailbox/components/new-message/fields/RecipientSelector';
import ScGroupField from '@src/main/modules/messages/ui/mailbox/components/new-message/fields/ScGroupField';
import ScListField from '@src/main/modules/messages/ui/mailbox/components/new-message/fields/ScListField';
import SubfleetsField from '@src/main/modules/messages/ui/mailbox/components/new-message/fields/SubfleetsField';
import { SubjectInput } from '@src/main/modules/messages/ui/mailbox/components/new-message/fields/SubjectInput';
import { RecipientType } from '@src/main/modules/messages/ui/mailbox/components/new-message/types/recipient.types';
import { useNewMessageForm } from '@src/main/modules/messages/ui/mailbox/hooks/new-message/useNewMessageForm';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './NewMessageForm.module.scss';

interface NewMessageFormProps {
  messageId?: string;
}

export const NewMessageForm: FC<NewMessageFormProps> = ({ messageId }) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    originalMessage,
    isLoadingOriginalMessage,
    recipientType,
    setScQuery,
    scOptions,
    fleetOptions,
    groupSCOptions,
    onSubmit,
    onInvalid,
    isSubmitting,
  } = useNewMessageForm(messageId);
  const { t } = useTranslation();

  useEffect(() => {
    if (originalMessage) {
      setValue('subject', `RE: ${originalMessage.subject}`);
    }
  }, [originalMessage, setValue]);

  const setPlainText = (text: string) => {
    setValue('messageText', text, { shouldValidate: true });
  };

  return (
    <AppLoading isLoading={isLoadingOriginalMessage && !!messageId}>
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className={styles['new-message-form']}>
        {originalMessage ? (
          <RecipientInput styles={styles} originalMessage={originalMessage} />
        ) : (
          <>
            <RecipientSelector control={control} styles={styles} />
            {recipientType === RecipientType.SUBFLEET && (
              <SubfleetsField control={control} fleetOptions={fleetOptions} styles={styles} t={t} />
            )}

            {recipientType === RecipientType.PLATE && (
              <PlateField control={control} styles={styles} t={t} />
            )}

            {recipientType === RecipientType.SC_LIST && (
              <ScListField
                control={control}
                styles={styles}
                scOptions={scOptions}
                setScQuery={setScQuery}
                t={t}
              />
            )}

            {recipientType === RecipientType.SC_GROUP && (
              <ScGroupField
                control={control}
                styles={styles}
                t={t}
                groupSCOptions={groupSCOptions}
              />
            )}

            {(recipientType === RecipientType.SUBFLEET ||
              recipientType === RecipientType.PLATE) && (
              <DateRangeFields styles={styles} control={control} watch={watch} />
            )}
          </>
        )}

        <SubjectInput control={control} styles={styles} />
        <MessageEditor control={control} setPlainText={setPlainText} styles={styles} />
        <FormButtons
          control={control}
          styles={styles}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          onInvalid={onInvalid}
          isLoading={isSubmitting}
        />
      </form>
    </AppLoading>
  );
};
